import { normRoles } from "@/routes/routeAccess";

const ROLE_LABELS: Record<string, string> = {
  MADAR_ADMIN: "Madar Admin",
  PROVIDER_ADMIN: "Provider Admin",
  DATA_ANALYST: "Data Analyst",
  FIELD_OPERATOR: "Field Operator",
  CLIENT: "Client",
  SALES_REPRESENTATIVE: "Sales Representative",
};

// Highest-priority first.
export const ROLE_PRIORITY: readonly string[] = [
  "MADAR_ADMIN",
  "PROVIDER_ADMIN",
  "DATA_ANALYST",
  "FIELD_OPERATOR",
  "SALES_REPRESENTATIVE",
  "CLIENT",
];

// "SOME_ROLE" -> "Some Role", for a role this list doesn't know yet.
const titleCase = (value: string) =>
  value
    .toLowerCase()
    .split(/[_\s]+/)
    .filter(Boolean)
    .map((word) => word[0].toUpperCase() + word.slice(1))
    .join(" ");

export function formatRoleLabels(roles: unknown): string[] {
  return normRoles(roles).map((role) => ROLE_LABELS[role] ?? titleCase(role));
}

// Stable sort, so unknown roles keep their stored order after the known ones.
export function sortRolesByPriority<T extends string>(roles: readonly T[]): T[] {
  const rank = (role: T) => {
    const index = ROLE_PRIORITY.indexOf(role);
    return index === -1 ? ROLE_PRIORITY.length : index;
  };

  return [...roles].sort((a, b) => rank(a) - rank(b));
}

// Readable labels in display order (highest-priority role first).
export function formatRoleLabelsInPriorityOrder(roles: unknown): string[] {
  return formatRoleLabels(sortRolesByPriority(normRoles(roles)));
}

export function formatStateLabel(state: unknown): string {
  const value = String(state ?? "").trim();
  return value ? titleCase(value) : "Unknown";
}

export function formatFullName(
  profile?: { firstName?: string | null; lastName?: string | null } | null,
): string {
  return `${profile?.firstName ?? ""} ${profile?.lastName ?? ""}`.trim();
}

export function formatLastSeen(value: unknown): string {
  if (value === null || value === undefined || value === "") return "Never";
  if (typeof value !== "string" && typeof value !== "number") return "Never";

  // DateTime can arrive as epoch milliseconds in a string.
  const date = new Date(
    typeof value === "string" && /^\d+$/.test(value) ? Number(value) : value,
  );
  if (Number.isNaN(date.getTime())) return "Never";

  return date.toLocaleString(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

/* ========= Validation (same rules as users-service) ========= */

export type NameFields = { firstName: string; lastName: string };
export type NameErrors = Partial<Record<keyof NameFields, string>>;

const NAME_PATTERN = /^[a-zA-Z-]+$/;

const nameError = (label: string, value: string): string | undefined => {
  if (!value) return `${label} is required`;
  if (value.length < 3) return `${label} must be at least 3 characters long`;
  if (!NAME_PATTERN.test(value))
    return `${label} can only contain letters and hyphens`;
  return undefined;
};

export function validateName({ firstName, lastName }: NameFields): NameErrors {
  const errors: NameErrors = {};
  const first = nameError("First name", firstName);
  const last = nameError("Last name", lastName);
  if (first) errors.firstName = first;
  if (last) errors.lastName = last;
  return errors;
}

export type PasswordFields = {
  currentPassword: string;
  newPassword: string;
  confirmPassword: string;
};
export type PasswordErrors = Partial<Record<keyof PasswordFields, string>>;

export const PASSWORD_MIN_LENGTH = 8;

// The reset password page's rules: at least 8 characters, and the
// confirmation must match. users-service also refuses a password of only
// spaces, or one equal to the current password.
export function validatePasswordChange({
  currentPassword,
  newPassword,
  confirmPassword,
}: PasswordFields): PasswordErrors {
  const errors: PasswordErrors = {};

  if (!currentPassword) errors.currentPassword = "Current password is required";

  if (!newPassword) {
    errors.newPassword = "New password is required";
  } else if (newPassword.length < PASSWORD_MIN_LENGTH) {
    errors.newPassword = `Password must be at least ${PASSWORD_MIN_LENGTH} characters long`;
  } else if (!/\S/.test(newPassword)) {
    errors.newPassword = "Password cannot be empty or spaces only";
  } else if (currentPassword && newPassword === currentPassword) {
    errors.newPassword =
      "New password must be different from the current password";
  }

  if (!confirmPassword) {
    errors.confirmPassword = "Please confirm your new password";
  } else if (newPassword !== confirmPassword) {
    errors.confirmPassword = "Passwords do not match";
  }

  return errors;
}
